import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import { Alert } from "reactstrap";
import { parseJwt } from "@/helpers/jwt_helper";

// Definimos el tipo para el contexto
interface AuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

// Creamos el contexto con un valor por defecto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Definimos las props para el AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const storedAuth = localStorage.getItem("isAuthenticated");
    const token = localStorage.getItem("token");

    const isTokenExpired = (token: string): boolean => {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp * 1000 < Date.now();
      } catch {
        return true;
      }
    };

    return storedAuth && token && !isTokenExpired(token) ? true : false;
  });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    localStorage.setItem("isAuthenticated", JSON.stringify(isAuthenticated));
  }, [isAuthenticated]);

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_API_URL}/admin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: username, password }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);

        const jwt = parseJwt(data.token);
        localStorage.setItem("modules", jwt.modules.join(","));
        localStorage.setItem("name", data.data?.name);

        setIsAuthenticated(true);
        setErrorMessage("");
        setSuccessMessage("Inicio de sesión exitoso");
        return true;
      } else {
        const data = await response.json();
        setErrorMessage(data.message || "Inicio de sesión fallido");
        setSuccessMessage("");
        return false;
      }
    } catch (error: any) {
      setErrorMessage("Error: " + error.message);
      setSuccessMessage("");
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_API_URL}/admin/logout`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setErrorMessage("");
        setSuccessMessage("Cierre de sesión exitoso");
      } else {
        setErrorMessage("Cierre de sesión fallido");
        setSuccessMessage("");
      }
    } catch (error: any) {
      setErrorMessage("Error: " + error.message);
      setSuccessMessage("");
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {errorMessage && (
        <Alert
          color="danger"
          style={{ position: "absolute", width: "100%", zIndex: "1" }}
        >
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert
          color="success"
          style={{ position: "absolute", width: "100%", zIndex: "1" }}
        >
          {successMessage}
        </Alert>
      )}
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

export { AuthContext, AuthProvider, useAuth };
